import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Modal, Nav, Spinner, Table } from 'react-bootstrap';
import SimpleBarReact from 'simplebar-react';
import { useSelector } from 'react-redux';
import SoftBadge from 'components/common/SoftBadge';
import RepositoryNotaryJournalsList from 'components/pages/admin/components/RepositoryNotaryJournalsList';
import { useGetNotaryAuditLogQuery } from 'rtk/org-admin/notaries.slice';
import c from 'util/const';
import { toBillingMoment } from 'util/organizationBillingFormat';

// Audit timestamps are UTC; show them in the signed-in user's profile timezone.
const formatCreatedAt = value => {
  if (!value) return '—';
  const m = toBillingMoment(value);
  return m.isValid() ? m.format('l, LT') : value;
};

const humanize = value =>
  String(value || '')
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const EVENT_VARIANTS = {
  REQUEST_SENT: 'primary',
  ACCEPTED: 'success',
  SHARED: 'info',
  REJECTED: 'danger',
  DECLINED: 'danger',
  REVOKED: 'warning'
};

const ACCESS_VARIANTS = {
  FULL: 'dark',
  SELECTIVE: 'primary',
  TIMEFRAME: 'warning',
  ENTRY: 'info',
  CONNECT: 'success'
};

const formatDescription = entry => {
  const description = entry?.Description;
  if (!description) return '—';
  const label = entry?.Entry?.Label;
  const guid = entry?.EntryGuid || entry?.Entry?.GUID;
  if (label && guid && description.includes(guid)) {
    return description.replace(guid, `"${label}"`);
  }
  return shortenGuids(description);
};

const formatDirection = value => {
  if (!value) return '—';
  return String(value).split('_TO_').map(humanize).join(' → ');
};

const shortenGuids = text =>
  String(text || '').replace(
    /\b([0-9a-f]{8})-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi,
    '$1…'
  );

const NotaryViewModal = ({ show, onHide, notaryUserGuid, notaryName }) => {
  const [activeTab, setActiveTab] = useState('audit');
  const signingDetailsOpen = useSelector(state =>
    state.modal?.modals?.some(
      modal => modal.modalType === c.modal.viewEntryDialog
    )
  );
  const modalVisible = show && !signingDetailsOpen;

  const {
    data: auditLog = [],
    isFetching: auditFetching,
    isLoading: auditIsLoading,
    isError: auditError,
    error: auditQueryError
  } = useGetNotaryAuditLogQuery(notaryUserGuid, {
    skip: !show || !notaryUserGuid
  });

  const auditLoading = auditFetching || auditIsLoading;
  const auditAccessUnavailable =
    auditError &&
    (auditQueryError?.Code === 'NotAdmin' || auditQueryError?.status === 403);
  const auditListError = auditError
    ? (typeof auditQueryError?.Data === 'string' && auditQueryError.Data) ||
      auditQueryError?.Message ||
      auditQueryError?.Text ||
      'Failed to load audit log.'
    : null;

  React.useEffect(() => {
    if (show) {
      setActiveTab('audit');
    }
  }, [show, notaryUserGuid]);

  return (
    <Modal
      show={modalVisible}
      onHide={onHide}
      size="xl"
      scrollable
      className="notary-view-modal"
      backdropClassName="notary-view-modal-backdrop"
      enforceFocus={!signingDetailsOpen}
    >
      <Modal.Header closeButton>
        <Modal.Title>{notaryName || 'Notary'}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-0 pb-3">
        <Nav
          variant="tabs"
          activeKey={activeTab}
          onSelect={key => key && setActiveTab(key)}
          className="px-3"
        >
          <Nav.Item>
            <Nav.Link eventKey="audit">Audit Log</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="journals">Journal Entries</Nav.Link>
          </Nav.Item>
        </Nav>

        {activeTab === 'audit' && (
          <div className="pt-3">
            {auditLoading ? (
              <div className="d-flex justify-content-center py-5">
                <Spinner animation="border" size="sm" />
              </div>
            ) : null}

            {auditListError && auditAccessUnavailable ? (
              <div className="text-center text-600 py-5 px-3">
                <p className="fw-semi-bold mb-1">No audit log available</p>
                <p className="fs--1 mb-0">{auditListError}</p>
              </div>
            ) : null}

            {auditListError && !auditAccessUnavailable ? (
              <Alert variant="danger" className="mx-3 fs--1">
                {auditListError}
              </Alert>
            ) : null}

            {!auditLoading && !auditListError && auditLog.length === 0 ? (
              <p className="text-center text-600 py-5 mb-0">
                No audit log entries.
              </p>
            ) : null}

            {!auditLoading && !auditListError && auditLog.length > 0 ? (
              <SimpleBarReact style={{ maxHeight: '60vh' }}>
                <Table hover responsive className="fs--1 mb-0">
                  <thead className="bg-200 text-900 text-nowrap align-middle">
                    <tr>
                      <th scope="col" className="ps-3">
                        Date
                      </th>
                      <th scope="col">Event</th>
                      <th scope="col">Direction</th>
                      <th scope="col">Access</th>
                      <th scope="col" className="pe-3">
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLog.map(entry => {
                      const access = entry?.AccessType || entry?.RequestType;
                      return (
                        <tr
                          key={
                            entry?.GUID ||
                            `${entry?.CreatedAt}-${entry?.EventType}`
                          }
                          className="align-middle"
                        >
                          <td className="ps-3 text-nowrap text-700">
                            {formatCreatedAt(entry?.CreatedAt)}
                          </td>
                          <td>
                            <SoftBadge
                              pill
                              bg={
                                EVENT_VARIANTS[entry?.EventType] || 'secondary'
                              }
                            >
                              {humanize(entry?.EventType) || '—'}
                            </SoftBadge>
                          </td>
                          <td className="text-nowrap">
                            {formatDirection(entry?.Direction)}
                          </td>
                          <td>
                            {access ? (
                              <SoftBadge
                                pill
                                bg={
                                  ACCESS_VARIANTS[
                                    String(access).toUpperCase()
                                  ] || 'secondary'
                                }
                              >
                                {humanize(access)}
                              </SoftBadge>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td
                            className="pe-3"
                            title={entry?.Description || undefined}
                          >
                            {formatDescription(entry)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </SimpleBarReact>
            ) : null}
          </div>
        )}

        {activeTab === 'journals' && (
          <div className="pt-3 px-3">
            <RepositoryNotaryJournalsList
              userGuid={notaryUserGuid}
              skip={!show || !notaryUserGuid || activeTab !== 'journals'}
              viewEntrySource="admin-notary-user-journals"
              scrollMaxHeight="60vh"
              pageSizeOptions={[10]}
              showEmptyState
            />
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

NotaryViewModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  notaryUserGuid: PropTypes.string,
  notaryName: PropTypes.string
};

NotaryViewModal.defaultProps = {
  notaryUserGuid: '',
  notaryName: ''
};

export default NotaryViewModal;
